/**
 * BadgeCard — iter110 · CEO 2026-09-12 · iter110d · thumbnail preview
 *
 * Carte affichée sur la fiche candidat quand le collaborateur est `hired`.
 * Permet à l'admin de :
 *   1. Choisir/modifier l'association mandataire (Amnesty / CCFD / HI / CDE)
 *   2. Prévisualiser la photo badge uploadée (spot-check du cadrage avant impression)
 *   3. Télécharger le badge PDF (A4 paysage, 4 badges identiques par feuille)
 *
 * Prérequis affichés en clair si non remplis : contrat signé + code_rd +
 * association + photo badge onboardée. Le backend renvoie 400 avec le
 * message exact si un prérequis manque.
 */
import { BadgeCheck, Download, Loader2, AlertTriangle, FileText, Image as ImageIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../lib/api";


export default function BadgeCard({ candidate, onChange }) {
    const [associations, setAssociations] = useState([]);
    const [selected, setSelected] = useState(candidate?.association || "");
    const [saving, setSaving] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [photoErr, setPhotoErr] = useState(false);

    useEffect(() => {
        api.get("/candidates/associations/list")
            .then(({ data }) => setAssociations(data.associations || []))
            .catch(() => setAssociations([]));
    }, []);

    useEffect(() => {
        setSelected(candidate?.association || "");
    }, [candidate?.association]);

    // Fetch photo thumbnail via authenticated blob URL. The image tag can't
    // carry the Bearer token, so we go through axios and objectURL.
    useEffect(() => {
        if (!candidate?.id) return undefined;
        let objectUrl = null;
        let cancelled = false;
        setPhotoErr(false);
        setPhotoUrl(null);
        api.get(`/candidates/${candidate.id}/badge-photo`, { responseType: "blob" })
            .then(({ data }) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(data);
                setPhotoUrl(objectUrl);
            })
            .catch(() => { if (!cancelled) setPhotoErr(true); });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [candidate?.id, candidate?.onboarding?.form?.documents?.badge_photo?.storage_path]);

    const saveAssociation = async (value) => {
        setSaving(true);
        try {
            await api.patch(`/candidates/${candidate.id}/association`, {
                association: value || null,
            });
            toast.success(value ? "Association mise à jour" : "Association effacée");
            onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de sauvegarder");
        } finally {
            setSaving(false);
        }
    };

    const handleAssociationChange = (e) => {
        const value = e.target.value;
        setSelected(value);
        saveAssociation(value);
    };

    // Prérequis pour le badge — mêmes règles que le backend
    const hasContract = Boolean(candidate?.contract_signed_at || candidate?.contract_signed_pdf_path);
    const hasCodeRd = Boolean(candidate?.code_rd);
    const hasAssociation = Boolean(candidate?.association);
    const hasBadgePhoto = Boolean(
        candidate?.onboarding?.form?.documents?.badge_photo?.storage_path ||
        candidate?.onboarding?.form?.documents?.BADGE_PHOTO?.storage_path
    );
    const allOk = hasContract && hasCodeRd && hasAssociation && hasBadgePhoto;

    const downloadBadge = async () => {
        setDownloading(true);
        try {
            // CEO 2026-09-30 · `?_t=` cache-buster + `association=` explicite
            // pour éviter que le navigateur (ou un intermédiaire) serve une
            // ancienne réponse quand l'utilisateur change d'association.
            const response = await api.get(`/candidates/${candidate.id}/badge.pdf`, {
                params: { _t: Date.now(), association: selected || undefined },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
            const link = document.createElement("a");
            const assoSlug = selected ? `_${selected}` : "";
            link.href = url;
            link.download = `badge_${candidate.code_rd || candidate.id.slice(0, 8)}_${candidate.last_name || "candidat"}${assoSlug}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            const assoLabel = associations.find((a) => a.value === selected)?.label || selected || "";
            toast.success("Badge téléchargé", assoLabel ? { description: assoLabel } : undefined);
        } catch (e) {
            // Extract error detail from blob response (axios returns blob)
            let detail = "Erreur inconnue";
            if (e.response?.data instanceof Blob) {
                try {
                    const text = await e.response.data.text();
                    detail = JSON.parse(text).detail || detail;
                } catch { /* silencieux */ }
            } else {
                detail = e.response?.data?.detail || e.message;
            }
            toast.error(detail);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="card-soft p-6 md:p-7" data-testid="badge-card">
            <div className="flex items-center gap-2 mb-4">
                <BadgeCheck className="h-5 w-5 text-shariti-orange" />
                <h3 className="text-lg font-extrabold tracking-tight">Badge collaborateur</h3>
                <span className="ml-auto text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-shariti-cream text-slate-500 border border-shariti-gray">
                    Impression rapide
                </span>
            </div>

            {/* Association dropdown */}
            <div className="mb-5">
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                    Association mandataire <span className="text-rose-500">*</span>
                </label>
                <select
                    value={selected}
                    onChange={handleAssociationChange}
                    disabled={saving}
                    data-testid="badge-association-select"
                    className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-semibold focus:border-shariti-orange focus:outline-none disabled:opacity-50"
                >
                    <option value="">— Non renseignée —</option>
                    {associations.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                </select>
                <p className="text-[10px] text-slate-400 mt-1">
                    Modifiable — sert à générer le badge avec le bon logo et les bonnes couleurs.
                </p>
            </div>

            {/* Prérequis */}
            <div className="mb-4 space-y-1.5">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                    Prérequis pour générer le badge
                </div>
                <Checkline ok={hasContract} label="Contrat signé" testid="badge-req-contract" />
                <Checkline ok={hasCodeRd} label={`Code collaborateur${candidate?.code_rd ? ` (${candidate.code_rd})` : ""}`} testid="badge-req-code" />
                <Checkline ok={hasAssociation} label="Association renseignée" testid="badge-req-asso" />
                <Checkline ok={hasBadgePhoto} label="Photo badge uploadée (onboarding)" testid="badge-req-photo" />
            </div>

            {/* Photo Preview — thumbnail à contrôler avant impression.
                Affichée dès qu'une photo est dispo (indépendamment des autres
                prérequis) pour que la RH puisse valider le cadrage tôt. */}
            {(photoUrl || (hasBadgePhoto && !photoErr)) && (
                <div className="mb-4" data-testid="badge-photo-preview-wrap">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5">
                        <ImageIcon className="h-3 w-3" />
                        Aperçu photo · à valider avant impression
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="w-[84px] h-[105px] rounded-xl overflow-hidden border-2 border-slate-200 bg-slate-50 shrink-0">
                            {photoUrl ? (
                                <img
                                    src={photoUrl}
                                    alt="Photo badge"
                                    data-testid="badge-photo-thumbnail"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                                </div>
                            )}
                        </div>
                        <div className="text-[11px] text-slate-500 leading-snug pt-0.5">
                            Cadrage portrait, visage bien lisible, fond neutre.
                            Si la photo est mal cadrée, redemandez-la via le formulaire
                            d'onboarding avant d'imprimer.
                        </div>
                    </div>
                </div>
            )}

            {/* CTA Download */}
            <button
                onClick={downloadBadge}
                disabled={!allOk || downloading}
                data-testid="badge-download-btn"
                className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-black transition ${
                    allOk
                        ? "bg-shariti-orange text-white hover:bg-shariti-orange-hover shadow-md"
                        : "bg-slate-100 text-slate-400 cursor-not-allowed"
                }`}
            >
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {downloading ? "Génération…" : "Télécharger le badge PDF"}
            </button>

            {!allOk && (
                <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-amber-800 font-semibold leading-snug">
                        Complète les prérequis manquants ci-dessus pour activer le téléchargement.
                        La photo badge s'upload via le formulaire d'embauche envoyé au collaborateur.
                    </p>
                </div>
            )}

            <div className="mt-3 text-[10px] text-slate-400 flex items-start gap-1.5">
                <FileText className="h-3 w-3 shrink-0 mt-0.5" />
                Format : A4 paysage, 4 badges identiques par feuille — imprimer en recto/verso puis découper le long des pointillés.
            </div>
        </div>
    );
}


function Checkline({ ok, label, testid }) {
    return (
        <div className="flex items-center gap-2 text-[12px]" data-testid={testid}>
            <span className={`inline-flex items-center justify-center h-4 w-4 rounded-full text-[10px] font-black ${
                ok ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
            }`}>
                {ok ? "✓" : "•"}
            </span>
            <span className={ok ? "text-slate-700 font-semibold" : "text-slate-400"}>{label}</span>
        </div>
    );
}
